// receiptUploadQueue.js - Fase 17.13 (2026-02-27)
// Robust upload-kø for kvitteringer. Persisteres i localStorage så den
// overlever browser-luk, dårlig dækning og iOS Safari memory-pressure.
//
// Design:
//   - Hver pending upload ligger i en kø under nøglen `cf:receiptQueue`
//   - Når et item enqueues, prøver vi straks at uploade i baggrunden
//   - Ved success fjernes item fra køen
//   - Retry sker ved app-mount, når nettet kommer tilbage, når browser-fanen
//     bliver synlig igen og hvert 30. sekund
//
// 2026-10-25 (S1): Ingen vejeseddel forsvinder uden at sige det.
//   - Billedet ligger i IndexedDB (blobStore) - køen i localStorage er kun små poster.
//   - Netværksfejl og serverfejl (5xx) tæller ikke som forsøg - før blev en
//     vejeseddel opgivet efter 5 åbninger af appen på en plads uden dækning.
//   - 401 (login udløbet): venter på et nyt login.
//   - Kun når serveren afviser sedlen (4xx), markeres den som fejlet. Den vises
//     med "Send igen" og "Fjern" (pendingReceipts / retryReceipt / removeReceipt).
//   - Hver seddel hører til den chauffør, der tog den (uid).
//   - Fuld lagerplads: den nye seddel afvises med en besked - de ventende slettes aldrig.
//
// Sikkerhed: Vi gemmer billede + tour_id + vægt midlertidigt på telefonen -
// kun mens uploaden venter. Ingen JWT/secrets gemmes her (axios sender det
// automatisk via Authorization-header fra in-memory state).

import axios from "axios";
import { putBlob, getBlob, delBlob } from "./blobStore";
import { currentUid, isNetworkError } from "./tourOpQueue";

const QUEUE_KEY = "cf:receiptQueue";
const RETRY_BACKOFF_MS = [0, 2000, 5000, 15000, 60000]; // 0s, 2s, 5s, 15s, 60s (derefter hvert minut)

let _isProcessing = false;
let _listeners = [];
let _pausedFor = "";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeader = () => String(axios.defaults.headers.common?.Authorization || "");
const mine = (i) => !i.uid || i.uid === currentUid();

function loadQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (_) { return []; }
}

function saveQueue(q) {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(q));   // kaster ved fuld lagerplads - intet slettes
}

function notifyListeners(event) {
  const count = getQueueCount();
  _listeners.forEach(fn => { try { fn(count, event); } catch (_) {} });
}

export function subscribeQueue(listener) {
  _listeners.push(listener);
  // Returnér unsubscribe.
  return () => { _listeners = _listeners.filter(l => l !== listener); };
}

export function getQueueCount() {
  return loadQueue().filter(i => !i.permanentFailure && mine(i)).length;
}

/** Chaufførens sedler, der venter eller er afvist (uden billedet). */
export function pendingReceipts() {
  return loadQueue().filter(mine).map(({ image_data, ...rest }) => rest);
}

const FULL_MSG = "Telefonen har ikke plads til flere billeder. Find net, så sendes de, der venter - og tag så billedet igen.";

/** Svarer køens id, eller null (og en besked), når telefonen er fuld. */
export async function enqueueReceiptUpload(payload) {
  const queue = loadQueue();
  // Dedupe: hvis et item med samme tour_id + samme final_weight_kg +
  // samme image-prefix allerede ligger i køen, springer vi over.
  const imgPrefix = (payload.image_data || "").slice(0, 64);
  const dup = queue.find(i =>
    i.tour_id === payload.tour_id &&
    i.final_weight_kg === payload.final_weight_kg &&
    (i.img_sig || (i.image_data || "").slice(0, 64)) === imgPrefix &&
    i.append_for_loose === payload.append_for_loose
  );
  if (dup) return dup.queueId;
  const queueId = `q_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const ref = `rcpt:${queueId}`;
  try { await putBlob(ref, payload.image_data || ""); } catch (_) { notifyListeners({ type: "full", message: FULL_MSG }); return null; }
  const { image_data, ...rest } = payload;
  const q = loadQueue();
  q.push({
    queueId,
    ...rest,
    image_ref: ref,
    img_sig: imgPrefix,
    uid: currentUid(),
    attempts: 0,
    queuedAt: Date.now(),
    permanentFailure: false,
    lastError: null,
  });
  try { saveQueue(q); } catch (_) { delBlob(ref); notifyListeners({ type: "full", message: FULL_MSG }); return null; }
  notifyListeners();
  // Start processing i baggrunden - ikke async/await så caller ikke blokeres.
  setTimeout(() => processQueue(), 0);
  return queueId;
}

async function uploadOne(item) {
  const image = item.image_data || (item.image_ref ? await getBlob(item.image_ref) : "");
  if (!image) { const e = new Error("Billedet findes ikke længere på telefonen"); e.response = { status: 410, data: { detail: e.message } }; throw e; }
  const body = {
    tour_id: item.tour_id,
    image_data: image,
    content_type: item.content_type || "image/jpeg",
    description: item.description || "Vedhæftet via scanner",
    final_weight_kg: item.final_weight_kg,
    user_name: item.user_name || "",
    append_for_loose: !!item.append_for_loose,
    skip_tour_update: true,
  };
  await axios.post(`${API}/receipts/save-for-tour`, body, { timeout: 60000 });
}

const update = (queueId, patch) => {
  const q = loadQueue();
  const it = q.find(x => x.queueId === queueId);
  if (it) { Object.assign(it, patch); try { saveQueue(q); } catch (_) { /* ignore */ } }
};

export async function processQueue() {
  if (_isProcessing) return;
  if (!authHeader() || (_pausedFor && _pausedFor === authHeader())) return;
  _isProcessing = true;
  try {
    for (const item of loadQueue().filter(mine)) {
      if (item.permanentFailure) continue;
      const backoff = RETRY_BACKOFF_MS[Math.min(item.attempts || 0, RETRY_BACKOFF_MS.length - 1)];
      if (Date.now() - (item.lastAttemptAt || 0) < backoff) continue;
      update(item.queueId, { lastAttemptAt: Date.now(), attempts: (item.attempts || 0) + 1 });
      try {
        await uploadOne(item);
        try { saveQueue(loadQueue().filter(x => x.queueId !== item.queueId)); } catch (_) { /* ignore */ }
        if (item.image_ref) delBlob(item.image_ref);
        notifyListeners({ type: "sent", item });
      } catch (e) {
        const lastError = e?.response?.data?.detail || e?.message || "ukendt fejl";
        // Login udløbet: venter på et nyt login - og sendes så med det samme (tæller ikke som forsøg).
        if (e?.response?.status === 401) { _pausedFor = authHeader(); update(item.queueId, { lastError, attempts: item.attempts || 0, lastAttemptAt: 0 }); break; }
        if (isNetworkError(e)) { update(item.queueId, { lastError }); break; }   // stadig uden net - næste gang
        update(item.queueId, { lastError: typeof lastError === "string" ? lastError : "Serveren afviste vejesedlen", permanentFailure: true });
        notifyListeners({ type: "failed", item });
      }
    }
  } finally {
    _isProcessing = false;
    notifyListeners();
  }
}

/** "Send igen" på en afvist seddel. */
export function retryReceipt(queueId) {
  update(queueId, { permanentFailure: false, attempts: 0, lastAttemptAt: 0, lastError: null });
  notifyListeners();
  return processQueue();
}

/** "Fjern" en afvist seddel (efter chaufførens bekræftelse). */
export function removeReceipt(queueId) {
  const it = loadQueue().find(x => x.queueId === queueId);
  try { saveQueue(loadQueue().filter(x => x.queueId !== queueId)); } catch (_) { /* ignore */ }
  if (it?.image_ref) delBlob(it.image_ref);
  notifyListeners();
}

// Startup hook: registrér retry-triggers på app-load.
let _started = false;
export function initReceiptUploadQueue() {
  if (typeof window === "undefined" || _started) return;
  _started = true;
  // Retry når browser-fanen igen bliver synlig (chauffør åbner appen).
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") processQueue();
  });
  // Retry når netværk vender tilbage.
  window.addEventListener("online", () => processQueue());
  setInterval(() => processQueue(), 30000);
  // Førstegangs-retry kort efter mount.
  setTimeout(() => processQueue(), 1000);
}
